import * as _ from 'underscore';

import { showRouterError } from '../../app/actions/spaces/RouterErrorActions';
import { appStore } from '../../app/AppStore';
import { PermissionLockStatus } from '../../app/content/Permissions/PermissionLockStatus';
import { Messages } from '../../codegen/Localize';
import { UserAgentSupport } from '../../common/utilities/UserAgentSupport';
import { FeatureFlags } from '../FeatureFlags';
import { EffectivePermissionsFetcher } from '../fetchers/EffectivePermissionsFetcher';
import { ListResult } from '../fetchers/ListResult';
import { AccordionState, SingleSectionAccordion, StaticAccordion } from '../helpers/AccordionState';
import { CommonErrorCode } from '../helpers/ErrorCodes';
import { Filter } from '../helpers/Filter';
import { PermissionsEditability, PermissionsEditabilityLogic } from '../helpers/permissions/PermissionsEditabilityLogic';
import { PermissionsEditabilityLogicFactory } from '../helpers/permissions/PermissionsEditabilityLogicFactory';
import { PermissionsHelper } from '../helpers/PermissionsHelper';
import {
  PermissionsHeaderTextAccordion,
  PermissionsPanelElementController,
  PermissionsPanelResizer,
} from '../helpers/PermissionsPanelResizer';
import { L10n } from '../L10n/L10n';
import { ColumnWidths } from '../models/ColumnWidths';
import { DatabaseContentType, ProjectContentType } from '../models/ContentTypes';
import { IGranteePermissions } from '../models/IGranteePermissions';
import { ISliceable } from '../models/ISliceable';
import { PagedList } from '../models/PagedList';
import { IPermission, Permission, PermissionReason } from '../models/Permission';
import { PermissionRoles, SpecialRoleNames } from '../models/PermissionRoles';
import { getPermissionsPanelColumnWidths } from '../models/PermissionsPanelColumnWidths';
import { INamedItemInfo, ITypedItemInfo } from '../models/resourceInfoParts';
import { Set } from '../models/Set';
import { ExplicitPermissions, ExplicitPermissionsResult } from '../resources/ExplicitPermissions';
import { Projects } from '../resources/Projects';
import {
  GetUsersGroupMembershipRequest,
  IGetUsersGroupMembershipResult,
} from '../ServerApi/requests/GetUsersGroupMembershipRequest';
import { Capabilities } from '../ServerApi/types/Capabilities';
import { IFilterClause } from '../ServerApi/types/IFilterClause';
import { IGetItemsParams } from '../ServerApi/types/IGetItemsParams';
import { IGroup } from '../ServerApi/types/IGroup';
import { IGroupMember } from '../ServerApi/types/IGroupMember';
import { ITypedIds } from '../ServerApi/types/ITypedIds';
import { IUserName, UserKeys } from '../ServerApi/types/IUser';
import { EffectivePermissionsService } from '../services/EffectivePermissionsService';
import { GranteePermissionsFactory } from '../services/GranteePermissionsFactory';
import { IPermissionsActionButton, PermissionsPanelActionsFactory } from '../services/PermissionsPanelActions';
import { ServerService } from '../services/ServerService';
import { SliceableFactory } from '../services/SliceableFactory';
import { PermissionRuleEditorCommand } from './PermissionRuleEditorCtrl';
import { IScrollControls, ScrollCtrl } from './ScrollCtrl';

export interface IPermissionsPanelScope extends ng.IScope {
  templateType: string;
  authorizableType: string;
  authorizables: Set<INamedItemInfo>; // the set of selected items the user has tried to view permissions of
  showPrompt: boolean;

  // The actual list of items we are showing permissions of.
  // It is the subset of the authorizables that where selected that the user has permission to view/edit.
  currentAuthorizables: ITypedIds;

  haveCurrentAuthorizable: () => boolean;
  explicitPermissions: ISliceable<IGranteePermissions>;
  effectivePermissions: ISliceable<IGranteePermissions>;

  currentExplicitPermission: IGranteePermissions;

  columnWidths: ColumnWidths;

  granteeType: (row: IGranteePermissions) => string;
  granteeName: (row: IGranteePermissions) => string;
  explicitGranteeRole: (row: IGranteePermissions, contentType: string) => string;
  userStatusForRow: (row: IGranteePermissions, contentType: string) => string;
  capabilityValue: (row: IGranteePermissions, contentType: string, capability: string, defaultValue: string) => string;
  capabilityReason: (row: IGranteePermissions, contentType: string, capability: string, defaultValue: string) => string;
  capabilityTitleKey: (row: IGranteePermissions, contentType: string, capability: string, defaultValue: string) => string;

  getCapabilityValueProps: (row: IGranteePermissions) => IGranteePermissions;

  isGranteeInheritedProjectLeader: (row: IGranteePermissions) => boolean;
  isGranteeExplicitProjectLeader: (row: IGranteePermissions) => boolean;

  setCurrentExplicitPermission: (explicitPermission: IGranteePermissions) => any;
  toggleCurrentExplicitPermission: (explicitPermission: IGranteePermissions) => any;
  haveCurrentExplicitPermission: () => boolean;
  isCurrentExplicitPermission: (explicitPermission: IGranteePermissions) => boolean;
  isHoveredExplicitPermission: (explicitPermission: IGranteePermissions) => boolean;
  isHoveredEffectivePermission: (effectivePermission: IGranteePermissions) => boolean;
  isRelevantExplicitPermission: (explicitPermission: IGranteePermissions) => boolean;
  isNotRelevantEffectivePermission: (effectivePermission: IGranteePermissions) => boolean;
  isNotRelevantExplicitPermission: (explicitPermission: IGranteePermissions) => boolean;
  relevantCapabilityClass: (explicitPermission: IGranteePermissions, contentType: string, capability: string, capabilityName: string) => string;
  isCurrentEffectivePermission: (row: IGranteePermissions) => boolean;
  hasCurrentEffectivePermission(): boolean;
  isLoadingEffectivePermissions: boolean;
  effectiveRowHovered: (row: IGranteePermissions) => void;
  explicitRowHovered: (row: IGranteePermissions) => void;

  startResize: (yPos: number) => void;
  continueResize: (yPos: number) => void;

  effectivePermissionsSliceChanged: (newSlice: ListResult<IGranteePermissions>) => any;

  // permission rule editor
  // TODO: refactor these into a single variable,
  // but be careful of 'the dot problem' since show is used both ways.
  permissionRuleEditorParams: {
    action: PermissionRuleEditorCommand;
    show: boolean;
    rowIndex: number;
  };

  permissionsLockStatusProps: PermissionLockStatus.Props;

  hideRowMenu: () => any;
  savePermissionRuleTranslationID: string; // save/delete button text

  // search
  submitSearch: (searchText: string, $event?: JQueryEventObject) => void;

  refresh: () => ng.IPromise<any>;

  addUserOrGroupSelector: { isShown: boolean };

  addNewUserPermission: (user: IUserName) => any;
  addNewGroupPermission: (group: IGroup) => any;
  addNewPrincipalPermission: (name: ITypedItemInfo) => any;

  explicitPermissionsPanelControls: {
    scrollControls?: IScrollControls;
  };
  effectivePermissionsPanelControls: {
    scrollControls?: IScrollControls;
  };

  getTooltipText: ($event: JQueryEventObject) => string;
  getHeaderTooltipText: ($event: JQueryEventObject) => string;

  getGranteeRoleText: (row: IGranteePermissions, contentType: string) => string;
  getInheritedProjectLeaderIconTooltip: (row: IGranteePermissions) => string;

  scrollLeftChanged: (scrollLeft: number) => any;

  editability: PermissionsEditability;

  resizeElementCtrl: PermissionsPanelElementController;

  sectionGroupAccordion: AccordionState;
  headerTextAccordion: AccordionState;
  shortHeaderText: () => boolean;

  permissionsTemplateUrl: string;

  menuActions: IPermissionsActionButton[];

  // facade that wraps the call to columnWidths.resizer.continueResize in $scope.apply()
  resize: (xPos: number) => void;

  setHoveredCapabilityValue(getCapabilityCell: () => JQuery): void;
  clearHoveredCapabilityValue(): void;
  isRelevantHeader(name: string): boolean;

  scrollEditorIntoView(): void;
  permissionsDataGridRowHeight: number;

  searchInput: { text: string };

  topLevelProject: INamedItemInfo;

  flowsOnSiteEnabled: boolean;
}

class PermissionsTemplateUrl {
  static forTemplateType(templateType: string): string {
    return templateType + 'PermissionsPanel.html';
  }
}

export class PermissionsPanelCtrl {

  public static $inject = [
    '$scope',
    '$element',
    '$timeout',
    '$translate',
    'PermissionsPanelActionsFactory',
    'SliceableFactory',
    'ServerService',
    'ExplicitPermissions',
    'EffectivePermissionsService',
    'PermissionRoles',
    'PermissionsEditabilityLogicFactory',
    'GranteePermissionsFactory',
    'Projects'
  ];

  private effectivePermissionsFetcher: EffectivePermissionsFetcher;
  private hoveredEffectivePermission: IGranteePermissions;
  private hoveredExplicitPermission: IGranteePermissions;
  private groupMembership: _.Dictionary<_.Dictionary<boolean>>;
  private explicitPermissionsList: ListResult<IGranteePermissions>;

  private hoveredCapabilityValue: string;
  private permissionsPanelResizer: PermissionsPanelResizer;
  $onInit: () => void;

  constructor(
    private $scope: IPermissionsPanelScope,
    $element: JQuery,
    private $timeout: ng.ITimeoutService,
    $translate: ng.translate.ITranslateService,
    permissionsPanelActionsFactory: PermissionsPanelActionsFactory,
    private sliceableFactory: SliceableFactory,
    private server: ServerService,
    private explicitPermissions: ExplicitPermissions,
    effectivePermissionsService: EffectivePermissionsService,
    private permissionRoles: PermissionRoles,
    private permissionsEditabilityLogicFactory: PermissionsEditabilityLogicFactory,
    private granteePermissionsFactory: GranteePermissionsFactory,
            projectsResource: Projects
  ) {
    this.$onInit = () => {
      this.hoveredCapabilityValue = null;
      this.updateCurrentAuthorizables();
      this.effectivePermissionsFetcher = new EffectivePermissionsFetcher(effectivePermissionsService, this.$scope.currentAuthorizables);

      this.$scope.currentExplicitPermission = null;
      this.hoveredEffectivePermission = null;
      this.hoveredExplicitPermission = null;
      this.$scope.isLoadingEffectivePermissions = false;

      $scope.flowsOnSiteEnabled = server.flowsOnSiteEnabled();

      $scope.searchInput = { text: '' };
      $scope.permissionsTemplateUrl = PermissionsTemplateUrl.forTemplateType($scope.templateType);

      $scope.columnWidths = getPermissionsPanelColumnWidths($scope.authorizableType, $scope.flowsOnSiteEnabled);
      $scope.resize = (xPos: number) => {
        $scope.$apply(() => {
          $scope.columnWidths.resizer.continueResize(xPos);
        });
      };

      $scope.granteeType = PermissionsHelper.granteeType;
      $scope.granteeName = PermissionsHelper.granteeName;
      $scope.explicitGranteeRole = (row: IGranteePermissions, contentType: string) =>
        PermissionsHelper.explicitGranteeRole(row, contentType, this.permissionRoles);
      $scope.userStatusForRow = (row: IGranteePermissions, contentType: string) =>
        this.userStatusForRow(row, contentType);
      $scope.capabilityValue = PermissionsHelper.capabilityValue;
      $scope.capabilityReason = PermissionsHelper.capabilityReason;
      $scope.capabilityTitleKey = PermissionsHelper.capabilityTitleKey;

      $scope.getCapabilityValueProps = (row: IGranteePermissions) => {
        // WARNING: Do not return a new object or else Angular will go into an infinite loop!
        return row;
      };

      $scope.isGranteeInheritedProjectLeader = (row: IGranteePermissions) => {
        const projectPermissions = row.permissionsByContentType[ProjectContentType.name];
        const inheritedProjectLeaderCapability = projectPermissions && projectPermissions[Capabilities.inheritedProjectLeader];
        const value: Permission = inheritedProjectLeaderCapability && inheritedProjectLeaderCapability.permission;
        return value === Permission.Allowed;
      };

      $scope.isGranteeExplicitProjectLeader = (row: IGranteePermissions) => {
        const explicitGranteeRole = PermissionsHelper.explicitGranteeRole(row, ProjectContentType.name, this.permissionRoles);
        return explicitGranteeRole === Capabilities.projectLeader;
      };

      $scope.explicitPermissionsPanelControls = {};
      $scope.effectivePermissionsPanelControls = {};

      $scope.toggleCurrentExplicitPermission = (explicitPermission: IGranteePermissions) => {
        if ($scope.permissionRuleEditorParams.show) {
          // if the editor is open, don't change the selected explicit permission
          return;
        }

        $scope.currentExplicitPermission === explicitPermission ?
          this.setCurrentExplicitPermission(null) :
          this.setCurrentExplicitPermission(explicitPermission);
      };

      $scope.setCurrentExplicitPermission = (explicitPermission: IGranteePermissions) => this.setCurrentExplicitPermission(explicitPermission);

      $scope.haveCurrentExplicitPermission = () => !!this.$scope.currentExplicitPermission;
      $scope.isCurrentExplicitPermission = (explicitPermission: IGranteePermissions) => {
        return this.$scope.currentExplicitPermission === explicitPermission;
      };

      $scope.isHoveredExplicitPermission = (explicitPermission: IGranteePermissions) => {
        return this.hoveredExplicitPermission === explicitPermission;
      };

      $scope.isHoveredEffectivePermission = (effectivePermission: IGranteePermissions) => {
        return this.hoveredEffectivePermission === effectivePermission;
      };

      $scope.isRelevantExplicitPermission = (explicitPermission: IGranteePermissions) => {
        return this.isRelevantExplicitPermission(explicitPermission);
      };

      $scope.isNotRelevantExplicitPermission = (explicitPermission: IGranteePermissions) => this.isNotRelevantExplicitPermission(explicitPermission);
      $scope.isNotRelevantEffectivePermission = (effectivePermission: IGranteePermissions) => this.isNotRelevantEffectivePermission(effectivePermission);
      $scope.relevantCapabilityClass = (explicitPermission: IGranteePermissions, contentType: string, capability: string, capabilityName: string) => {
        return this.relevantCapabilityClass(explicitPermission, contentType, capability, capabilityName);
      };

      $scope.isRelevantHeader = (name: string) => {
        return this.hoveredCapabilityValue === name;
      };

      $scope.setHoveredCapabilityValue = (getCapabilityCell: () => JQuery) => {
        this.hoveredCapabilityValue = getCapabilityCell().attr('data-capability-name');

        // We don't usually digest on mouseenter and mouseleave, so force it
        this.$scope.$digest();
      };

      $scope.clearHoveredCapabilityValue = () => {
        this.hoveredCapabilityValue = null;

        // We don't usually digest on mouseenter and mouseleave, so force it
        this.$scope.$digest();
      };

      $scope.isCurrentEffectivePermission = (row: IGranteePermissions) => {
        return this.hoveredEffectivePermission === row;
      };

      $scope.hasCurrentEffectivePermission = () => !!this.hoveredEffectivePermission;

      $scope.effectiveRowHovered = (row: IGranteePermissions) => {
        this.hoveredEffectivePermission = row;

        // We don't usually digest on mouseenter and mouseleave, so force it
        this.$scope.$digest();
      };

      $scope.explicitRowHovered = (row: IGranteePermissions) => {
        this.hoveredExplicitPermission = row;

        // We don't usually digest on mouseenter and mouseleave, so force it
        this.$scope.$digest();
      };

      $scope.startResize = (yPos: number) => {
        return this.permissionsPanelResizer.startResize(yPos);
      };

      $scope.continueResize = (yPos: number) => {
        return this.permissionsPanelResizer.continueResize(yPos);
      };

      $scope.effectivePermissionsSliceChanged = (newSlice: ListResult<IGranteePermissions>) => {
        const userIds = _.map(newSlice.items, (item: IGranteePermissions) => item.user.id);
        this.updateGroupMembership(userIds);
        this.$timeout(() => {
          if (this.$scope.effectivePermissionsPanelControls.scrollControls) {
            const scrollLeft = this.$scope.explicitPermissionsPanelControls.scrollControls.getScrollLeft();
            this.$scope.effectivePermissionsPanelControls.scrollControls.setScrollLeft(scrollLeft);
          }
        });
      };

      $scope.hideRowMenu = () => {
        this.hidePermissionRuleEditor();
      };

      $scope.submitSearch = (searchText: string, $event?: JQueryEventObject) => {
        if (!this.explicitPermissionsList) {
          return;
        }

        if (this.$scope.currentExplicitPermission !== this.explicitPermissionsList.items[0]) {
          // the All Users group is not selected.  Select it.
          this.setCurrentExplicitPermission(this.explicitPermissionsList.items[0]);
        }
        this.updateEffectivePermissions(searchText);
        if (UserAgentSupport.isTouch() && $event) {
          $($event.target).trigger('blur');
        }
      };

      $scope.$watch('searchInput.text', (text: string) => {
        $scope.submitSearch(text);
      });

      $scope.permissionRuleEditorParams = {
        show: false,
        action: null,
        rowIndex: null
      };

      $scope.refresh = () => {
        this.updateCurrentAuthorizables();
        return this.updateExplicitPermissions(this.$scope.currentAuthorizables);
      };

      $scope.addUserOrGroupSelector = {
        isShown: false
      };

      $scope.addNewUserPermission = (user: IUserName) => {
        if (!user) {
          return;
        }
        this.addNewGranteePermissions(
          this.granteePermissionsFactory.createForUserWithUnspecifiedPermissions(
            user, this.$scope.authorizableType));
      };

      $scope.addNewGroupPermission = (group: IGroup) => {
        if (!group) {
          return;
        }
        this.addNewGranteePermissions(
          this.granteePermissionsFactory.createForGroupWithUnspecifiedPermissions(
            group, this.$scope.authorizableType));
      };


      $scope.addNewPrincipalPermission = (item: ITypedItemInfo) => {
        if (!item) {
          return;
        }
        this.addNewGranteePermissions(
          this.granteePermissionsFactory.createForPrincipalWithUnspecifiedPermissions(
            item, this.$scope.authorizableType));
      };

      $scope.haveCurrentAuthorizable = () => {
        return (this.$scope.authorizables.size() > 0);
      };

      $scope.getTooltipText = ($event: JQueryEventObject) => {
        return $($event.target)
          .closest('.tb-capability-value-cell')
          .find('.tb-capability-value')
          .attr('data-tooltip');
      };

      $scope.getHeaderTooltipText = ($event: JQueryEventObject) => {
        if ($scope.headerTextAccordion.isSectionOpen(0)) return null;
        return $($event.target)
          .closest('.tb-permissions-capability-header-icon-cell')
          .find('.tb-permissions-capability-icon')
          .attr('data-tooltip');
      };

      $scope.getGranteeRoleText = (row: IGranteePermissions, contentType: string) => {
        const explicitRole = $translate.instant($scope.explicitGranteeRole(row, contentType));
        const isProject = contentType === ProjectContentType.name;
        const isInheritedProjectLeader = $scope.isGranteeInheritedProjectLeader(row);
        const isExplicitProjectLeader = $scope.isGranteeExplicitProjectLeader(row);
        if (isProject && isInheritedProjectLeader && !isExplicitProjectLeader) {
          return Messages.inherited_project_role({
            EXPLICIT_ROLE: explicitRole
          });
        } else {
          return explicitRole;
        }
      };

      $scope.getInheritedProjectLeaderIconTooltip = (row: IGranteePermissions) => {
        const name = $scope.granteeName(row);
        return Messages.inherited_project_leader_tooltip({
          USER_NAME: name
        });
      };

      // synchronize x-scrolling of the effective and explicit permissions panels.
      $scope.scrollLeftChanged = (scrollLeft: number) => {

        // position the headers correctly relative to the explicit permissions body
        $element.find('.tb-explicit-permissions-headers-line').css({ 'left': -scrollLeft + 'px' });

        // make sure the background rows in both panels are rendered wide enough.
        // right: 0 isn't far enough when .tb-data-grid-body-columns is overflowing.
        $element.find('.tb-data-grid-body-background').css({ 'right': -scrollLeft + 'px' });

        this.$scope.explicitPermissionsPanelControls.scrollControls.setScrollLeft(scrollLeft);
        if (this.$scope.effectivePermissionsPanelControls.scrollControls) {
          this.$scope.effectivePermissionsPanelControls.scrollControls.setScrollLeft(scrollLeft);
        }
      };

      $scope.resizeElementCtrl = new PermissionsPanelElementController($element);
      this.permissionsPanelResizer = new PermissionsPanelResizer($scope.resizeElementCtrl);

      // When the top panel and the add permissions rule element get rendered for the first time,
      // set the top panel height accordingly
      const unregisterTopPanelWatch = $scope.$watch(() => $element.find('.tb-permissions-top').length,
        (newValue) => {
          if (newValue > 0) {
            this.setInitialPanelHeight();
            unregisterTopPanelWatch();
          }
        });
      const unregisterAddRuleRowWatch = $scope.$watch(() => $element.find('.tb-add-permissions-rule-container').length,
        (newValue) => {
          if (newValue > 0) {
            this.setInitialPanelHeight();
            unregisterAddRuleRowWatch();
          }
        });

      $scope.sectionGroupAccordion = ($scope.authorizableType === ProjectContentType.name
        || $scope.authorizableType === DatabaseContentType.name)
        ? new SingleSectionAccordion() : StaticAccordion;

      $scope.headerTextAccordion = new PermissionsHeaderTextAccordion(
        $scope.resizeElementCtrl, PermissionsPanelCtrl.shortHeaderText);
      $scope.shortHeaderText = PermissionsPanelCtrl.shortHeaderText;

      const permissionsPanelActions = permissionsPanelActionsFactory.getPermissionsPanelActions(
        this.showPermissionRuleEditor.bind(this), this.getGranteeIndex.bind(this));
      $scope.menuActions = permissionsPanelActions.getActionButtons();

      // These prop values are filled after we've fetched source authorizable info, in updateExplicitPermissions.
      $scope.permissionsLockStatusProps = {
        lockState: null,
        sectionHeaderMessage: null,
      };

      // Only consider the top level project when displaying permissions for a single project.
      // When multiple projects are selected, it's possible they could come from different hierarchies and
      // thus have different top level projects. No affordance for this exists in the UI at this time.
      if ($scope.authorizables.size() === 1 && $scope.authorizableType === ProjectContentType.name) {
        projectsResource.getProjectHierarchy($scope.authorizables.first().id).then((result: INamedItemInfo[]) => {
          $scope.topLevelProject = result && result.length > 0 ? result[0] : $scope.authorizables.first();
        });
      }

      $scope.scrollEditorIntoView = () => {
        const index = this.getGranteeIndex($scope.currentExplicitPermission);
        $scope.explicitPermissionsPanelControls.scrollControls.scrollIntoView(index, index + 1);
      };

      $scope.refresh();

      // should match $permissionsGridTotalRowHeight in permissions.styl
      $scope.permissionsDataGridRowHeight = 28;
    };
  }

  private setInitialPanelHeight(): void {
    const sizeCtrl = this.$scope.resizeElementCtrl;
    sizeCtrl.findElements(); // since new elements were just rendered, refresh the stored references
    sizeCtrl.setTopPanelHeightAndUpdatePreferred(sizeCtrl.measurePermissionsPanelBodyHeight() * 0.5);
  }

  private static shortHeaderText(): boolean {
    switch (L10n.language) {
      case 'de': return false;
      case 'en': return true;
      case 'es': return false;
      case 'fr': return false;
      case 'ja': return false;
      case 'ko': return false;
      case 'pt': return false;
      case 'zh': return true;

      case 'de_DE': return false;
      case 'en_GB': return true;
      case 'en_US': return true;
      case 'es_ES': return false;
      case 'fr_FR': return false;
      case 'ja_JP': return false;
      case 'ko_KR': return false;
      case 'pt_BR': return false;
      case 'zh_CN': return true;
      case 'zh_TW': return true;
    }
    return false;
  }

  private setCurrentExplicitPermission(explicitPermission: IGranteePermissions) {
    if (this.$scope.currentExplicitPermission !== explicitPermission) {
      this.$scope.currentExplicitPermission = explicitPermission;
      this.updateEffectivePermissions();
    }
  }

  private addNewGranteePermissions(newGranteePermissions: IGranteePermissions): any {
    this.$scope.addUserOrGroupSelector.isShown = false;

    const explicitPermissions = this.explicitPermissionsList.items;
    const index = this.getGranteeIndex(newGranteePermissions);

    if (PermissionsHelper.hasSameGrantee(newGranteePermissions, explicitPermissions[index])) {

      this.startEditingRule(index, 'edit');

    } else {

      explicitPermissions.splice(index, 0, newGranteePermissions);
      this.$scope.explicitPermissions = this.sliceableFactory.fromArray(explicitPermissions);

      // Wait for the scroll control to see the new list before proceeding.
      const stopListening = this.$scope.$on(ScrollCtrl.ItemsChangeEvent, () => {
        stopListening();
        this.startEditingRule(index, 'add');
      });

    }
  }

  private getGranteeIndex(granteePermissions: IGranteePermissions): number {
    return _.sortedIndex(this.explicitPermissionsList.items, granteePermissions, (gp: IGranteePermissions) => {
      return PermissionsHelper.compareGrantees(gp, granteePermissions);
    });
  }

  private startEditingRule(index: number, action: PermissionRuleEditorCommand) {
    this.setCurrentExplicitPermission(this.explicitPermissionsList.items[index]);
    this.$scope.explicitPermissionsPanelControls.scrollControls.scrollIntoView(index);
    this.$timeout(this.showPermissionRuleEditor.bind(this, index, action));
  }

  private showPermissionRuleEditor(index: number, action: PermissionRuleEditorCommand) {
    this.$scope.permissionRuleEditorParams.action = action;
    this.$scope.permissionRuleEditorParams.show = true;
    this.$scope.permissionRuleEditorParams.rowIndex = index;
  }

  private hidePermissionRuleEditor() {
    this.$scope.permissionRuleEditorParams.show = false;
  }

  private updateCurrentAuthorizables(): void {
    this.$scope.currentAuthorizables = {
      type: this.$scope.authorizableType,
      ids: this.$scope.authorizables.keys()
    };
  }

  private updateExplicitPermissions(authorizables: ITypedIds): ng.IPromise<void> {
    return this.explicitPermissions.forAuthorizables(authorizables)
      .then((result: ExplicitPermissionsResult) => {
        if (result) {
          if (result.errors && _.find(result.errors, { code: CommonErrorCode.CallingUserInsufficientPermissions })) {
            appStore.dispatch(showRouterError('insufficient-permissions'));
          }

          const editabilityLogic: PermissionsEditabilityLogic =
            this.permissionsEditabilityLogicFactory.logicForAuthorizables(
              this.$scope.authorizables, this.$scope.authorizableType, result.sourceAuthorizablesById);
          this.$scope.editability = editabilityLogic.getEditabilityWithMessages();

          this.$scope.permissionsLockStatusProps = {
            lockState: this.$scope.editability.lockState,
            sectionHeaderMessage: this.$scope.editability.sectionHeaderMessage,
          };

          this.explicitPermissionsList = result.permissionsList;
          this.explicitPermissionsList.items.sort(PermissionsHelper.compareGrantees);
          this.effectivePermissionsFetcher.setAuthorizables(authorizables);
          this.updateExplicitPermissionsSlice();
          this.updateEffectivePermissions();
        }
      });
  }

  private setMissingCapabilitiesForSectionsToUnspecified(items: IGranteePermissions[]): void {
    _.each(PermissionsHelper.getPermissionsContentTypes(this.$scope.authorizableType, this.$scope.flowsOnSiteEnabled),
      (contentType: string) => {
        const capabilities = _.keys(this.permissionRoles.getPermissionsForTypeAndRole(contentType, SpecialRoleNames.None));
        _.each(items, (gp: IGranteePermissions) => {
          if (!_.has(gp.permissionsByContentType, contentType)) {
            gp.permissionsByContentType[contentType] = {};
          }
          const permissionsForContentType: _.Dictionary<IPermission> = gp.permissionsByContentType[contentType];
          _.each(capabilities, (capability: string) => {
            if (!_.has(permissionsForContentType, capability)) {
              permissionsForContentType[capability] = {
                permission: Permission.Unspecified,
                reason: PermissionReason.Unspecified
              };
            }
          });
        });
      }
    );
  }

  private updateExplicitPermissionsSlice() {
    const items = this.explicitPermissionsList.items;
    this.setMissingCapabilitiesForSectionsToUnspecified(items);

    this.$scope.explicitPermissions = this.sliceableFactory.fromArray(items);
    const current = this.$scope.currentExplicitPermission;
    if (current) {
      this.$scope.currentExplicitPermission =
        _.find(items, (item: IGranteePermissions) => PermissionsHelper.hasSameGrantee(item, current));
    }
  }

  private updateEffectivePermissions(searchText?: string) {
    this.$scope.isLoadingEffectivePermissions = true;
    this.hoveredEffectivePermission = null;
    const current = this.$scope.currentExplicitPermission;
    if (current) {
      const filterClauses: IFilterClause[] = [];
      // add a filter clause for the current group or user
      if (current.group) {
        filterClauses.push(Filter.stringArrayFieldHas('groupIds', current.group.id));
      } else if (current.user) {
        filterClauses.push(Filter.stringFieldEq('id', current.user.id));
      }
      // add a filter clause for the current search text
      if (searchText) {
        filterClauses.push(Filter.search(searchText));
      }
      // fetch the effective permissions
      const effectivePermissionsFetchParams: IGetItemsParams = {
        filter: Filter.and(filterClauses),
        order: [ { field: UserKeys.displayName, ascending: true } ]
      };
      this.$scope.effectivePermissions = new PagedList<IGranteePermissions>(
        this.effectivePermissionsFetcher, effectivePermissionsFetchParams
      );
    } else {
      this.$scope.effectivePermissions = this.sliceableFactory.fromArray();
    }
    this.$scope.isLoadingEffectivePermissions = false;
  }

  private updateGroupMembership(userIds: string[]) {
    if (userIds && userIds.length > 0) {
      this.server.sendRequest(new GetUsersGroupMembershipRequest({ userIds: userIds }))
        .then((result: IGetUsersGroupMembershipResult) => {
          this.groupMembership = {};
          _.each(result.members, (member: IGroupMember) => {
            let userGroups = this.groupMembership[member.userId];
            if (!userGroups) {
              userGroups = {};
              this.groupMembership[member.userId] = userGroups;
            }
            userGroups[member.groupId] = true;
          });
        });
    } else {
      this.groupMembership = {};
    }
  }

  private isRelevantExplicitPermission(explicitPermission: IGranteePermissions): boolean {
    if (this.hoveredEffectivePermission && !this.$scope.permissionRuleEditorParams.show) {
      const currentUser = this.hoveredEffectivePermission.user;
      if (currentUser) {
        if (explicitPermission.group) {
          // group rule
          if (this.groupMembership) {
            // is the user a member of this group?
            const userGroups = this.groupMembership[currentUser.id];
            return userGroups && _.has(userGroups, explicitPermission.group.id);
          }
        } else {
          // user rule
          return explicitPermission.user && (explicitPermission.user.id === currentUser.id);
        }
      }
    }
    return false;
  }

  /**
   * Tests if the current explicit permission is not relevant and thus should be visually greyed out.
   */
  private isNotRelevantExplicitPermission(explicitPermission: IGranteePermissions): boolean {
    return this.hoveredEffectivePermission && !this.isRelevantExplicitPermission(explicitPermission);
  }

  /**
   * Tests if the current effective permission is not relevant and thus should be visually greyed out.
   * We grey out effective permissions other than the one the user is currently hovering over.
   */
  private isNotRelevantEffectivePermission(effectivePermission: IGranteePermissions): boolean {
    return this.hoveredEffectivePermission && !this.$scope.isCurrentEffectivePermission(effectivePermission);
  }

  /**
   * returns the string 'relevant', 'normal', or 'not', which is used as a suffix to the class tb-relevant-capability-
   */
  private relevantCapabilityClass(
    explicitPermission: IGranteePermissions,
    contentType: string,
    capability: string,
    capabilityName: string): string {

    if (!this.hoveredEffectivePermission || this.$scope.permissionRuleEditorParams.show) {
      return 'normal';
    }

    if (explicitPermission && this.isRelevantExplicitPermission(explicitPermission)) {

      if (!this.hoveredCapabilityValue) {
        // When the user is hovering on an effective permissions row but not any capability value cell, we show all the cells as 'normal'
        return 'normal';
      } else if (this.hoveredCapabilityValue === capabilityName) {

        const currEffectivePermission =
          PermissionsHelper.permissionFromIGranteePermission(
            this.hoveredEffectivePermission, contentType, capability);

        const currExplicitPermission =
          PermissionsHelper.permissionFromIGranteePermission(
            explicitPermission, contentType, capability);

        if (currEffectivePermission && currExplicitPermission) {
          if (explicitPermission.group) {
            // group rule
            switch (currExplicitPermission.permission) {
              case Permission.Allowed: return (PermissionReason.GroupAllow === currEffectivePermission.reason) ? 'relevant' : 'not';
              case Permission.Denied: return (PermissionReason.GroupDeny === currEffectivePermission.reason) ? 'relevant' : 'not';
            }
          } else if (explicitPermission.user) {
            // user rule
            switch (currExplicitPermission.permission) {
              case Permission.Allowed: return (PermissionReason.UserAllow === currEffectivePermission.reason) ? 'relevant' : 'not';
              case Permission.Denied: return (PermissionReason.UserDeny === currEffectivePermission.reason) ? 'relevant' : 'not';
            }
          }
        }
      }

    }

    return 'not';
  }

  private userStatusForRow(row: IGranteePermissions, contentType: string): string {
    // In the PermissionReasons enum, superuser allows are the highest precedence
    // (except for 'mixed', but a superuser's permission reasons shouldn't be mixed.)
    // So we can ignore mixed, and infer superuser status from the highest-precedence reason.

    let maxReason: number = PermissionReason.Unspecified;
    const permissions = PermissionsHelper.permissionsFromIGranteePermission(row, contentType);
    _.each(permissions, (permission: IPermission) => {
      if (permission.reason < PermissionReason.Mixed) {
        maxReason = Math.max(maxReason, permission.reason);
      }
    });

    switch (maxReason) {
      case PermissionReason.UserAuthorizableOwnerAllow:   return 'owner';
      case PermissionReason.GroupProjectLeaderAllow:      return 'project_leader';
      case PermissionReason.UserProjectLeaderAllow:       return 'project_leader';
      case PermissionReason.UserProjectOwnerAllow:        return 'project_owner';
      case PermissionReason.UserAdminAllow:               return 'admin';
      default:
        return PermissionsHelper.effectiveGranteeRole(row, contentType, this.permissionRoles);
    }

  }

}
